/**
 * Public API of the Message Vault — minimal surface.
 *
 * Three groups:
 *   1. WRITE   — captureMessage()
 *   2. READ    — getMessage(), getMessageBySuffix(), countAll()
 *   3. ADMIN   — purgeVaultBeyondRetention(), wipeAll()
 *
 * Every method is async + defensive. The vault must never throw — a
 * single bad row should never break the chat UI. Every public entry
 * point funnels through `safe()` so this rule lives in one place.
 *
 * No `markDeleted`, no `addEditRevision`, no `revisions` table.
 * Detection of deletion is done by the injection layer scanning the
 * DOM — not by the vault. The vault only knows about bodies.
 */
import { getMessageVaultDb } from './messageVaultDb';
import { msgIdMatchesDomId } from './msgId-match';
import {
  isBucketMediaType,
  normalizeVaultRow,
  type BackedUpMessage,
  type PlaceholderMapping,
  type RecoveryResult,
  type VaultCaptureInput,
  type WaChatId,
  type WaMessageId,
} from './types';

const PREFIX = '[message-vault]';

const safe = async <T>(
  label: string,
  fn: () => Promise<T>,
  fallback: T,
): Promise<T> => {
  try {
    return await fn();
  } catch (err) {
    console.warn(`${PREFIX} ${label} failed:`, err);
    return fallback;
  }
};

/* ─────────────────────────────────────────────────────────────────── */
/* WRITE                                                                */
/* ─────────────────────────────────────────────────────────────────── */

/** Idempotent insert keyed on the canonical `dedupKey` (addressing-agnostic).
 *  If we already have this message under EITHER its @c.us or @lid addressing,
 *  do nothing — first capture wins, because that's when we saw the ORIGINAL
 *  body, and we store each message exactly once regardless of addressing. */
export const captureMessage = async (
  row: VaultCaptureInput,
): Promise<boolean> =>
  safe(
    'captureMessage',
    async () => {
      // Skip empty rows up front. System / protocol messages have no
      // body to recover, and we don't want them taking up vault space.
      // Voice notes are the exception: no text body, but the captured
      // audio bytes ARE the recoverable content.
      if (!row.msgId || (!row.body && !row.audioBase64)) {
        console.log('[bt-vault] capture SKIP — missing msgId or content', { msgId: row.msgId, hasBody: !!row.body, hasAudio: !!row.audioBase64 });
        return false;
      }
      // normalizeVaultRow derives dedupKey from msgId + defaults the backup
      // sync flags. Dedup on dedupKey so the @c.us/@lid variants of one message
      // collapse to a single row (the plain `msgId` differs between them).
      const normalized = normalizeVaultRow({
        ...row,
        capturedAt: Date.now(),
      }) as BackedUpMessage;
      const db = getMessageVaultDb();
      const existing = normalized.dedupKey
        ? await db.messages.where('dedupKey').equals(normalized.dedupKey).first()
        : await db.messages.where('msgId').equals(row.msgId).first();
      if (existing) {
        // console.log('[bt-vault] capture SKIP — already exists', normalized.dedupKey);
        return false;
      }
      await db.messages.add(normalized);
      console.log('[bt-vault] capture OK', { msgId: row.msgId, body: row.body.slice(0, 40) });
      return true;
    },
    false,
  );

/* ─────────────────────────────────────────────────────────────────── */
/* READ                                                                 */
/* ─────────────────────────────────────────────────────────────────── */

/** Direct lookup by full WA `_serialized` id. */
export const getMessage = async (
  msgId: WaMessageId,
): Promise<BackedUpMessage | null> =>
  safe(
    'getMessage',
    async () => {
      if (!msgId) return null;
      const db = getMessageVaultDb();
      const row = await db.messages.where('msgId').equals(msgId).first();
      return row ?? null;
    },
    null,
  );

/** Suffix-tolerant lookup.
 *
 *  WA's DOM `data-id` is sometimes the FULL serialized id and
 *  sometimes just the bare message-id (HEX) segment — depends on the
 *  WA build and chat type (@c.us vs @lid). The vault stores the full
 *  `_serialized` form. To find a row by whatever piece of the id WA
 *  exposes in the DOM:
 *    1. exact-match fast path (single indexed query)
 *    2. fall through to a whole-segment scan (see msgIdMatchesDomId)
 *
 *  IMPORTANT: in @lid GROUPS the serialized id is
 *  `<fromMe>_<remote>@g.us_<HEX>_<participant>@lid` — the HEX sits in
 *  the MIDDLE. An `endsWith(HEX)` scan (the previous approach) never
 *  matched those, so group-chat placeholders never resolved a backup
 *  and the Restore button never appeared. `msgIdMatchesDomId` matches
 *  the HEX as a complete underscore-delimited segment instead.
 *
 *  Cost: O(rows-in-table) on the scan path. The vault is bounded
 *  by retention so this stays in the low thousands at worst — sub-frame. */
export const getMessageBySuffix = async (
  idOrSuffix: string,
): Promise<BackedUpMessage | null> =>
  safe(
    'getMessageBySuffix',
    async () => {
      if (!idOrSuffix) return null;
      const db = getMessageVaultDb();
      // Exact path first — if WA gave us the full serialized id.
      const exact = await db.messages
        .where('msgId')
        .equals(idOrSuffix)
        .first();
      if (exact) return exact;
      // Fallback — whole-segment scan. Handles both the HEX-at-end
      // (1:1) and HEX-in-middle (group @lid) serialized-id shapes.
      const row = await db.messages
        .filter((m) => msgIdMatchesDomId(m.msgId, idOrSuffix))
        .first();
      return row ?? null;
    },
    null,
  );

/** Total row count — used by the Display Settings stats card. */
export const countAll = async (): Promise<number> =>
  safe(
    'countAll',
    async () => {
      const db = getMessageVaultDb();
      return db.messages.count();
    },
    0,
  );

/** Find the most recent captured message in a chat. Used as a last-
 *  resort fallback by the deletion bridge. */
export const getMostRecentInChat = async (
  chatId: WaChatId,
  excludeMsgIds: ReadonlyArray<WaMessageId> = [],
): Promise<BackedUpMessage | null> =>
  safe(
    'getMostRecentInChat',
    async () => {
      if (!chatId) return null;
      const db = getMessageVaultDb();
      const exclude = new Set(excludeMsgIds);
      const row = await db.messages
        .where('[chatId+capturedAt]')
        .between([chatId, 0], [chatId, Infinity])
        .reverse()
        .filter((m) => !exclude.has(m.msgId))
        .first();
      return row ?? null;
    },
    null,
  );

/** Return ALL captured messages in a chat whose WA timestamp falls in
 *  [lowerWaTsMs, upperWaTsMs], sorted ascending. Used by the slot-
 *  based matcher in the injection — when N revoked rows share the
 *  same gap between non-revoked anchors, this returns the captures
 *  in chronological order so each row can pick its slot. */
export const getCandidatesInWindow = async (
  chatId: WaChatId,
  lowerWaTsMs: number,
  upperWaTsMs: number,
  excludeMsgIds: ReadonlyArray<WaMessageId> = [],
  opts: { excludeTypes?: ReadonlyArray<string> } = {},
): Promise<BackedUpMessage[]> =>
  safe(
    'getCandidatesInWindow',
    async () => {
      if (!chatId) return [];
      if (!Number.isFinite(lowerWaTsMs)) lowerWaTsMs = 0;
      if (!Number.isFinite(upperWaTsMs)) upperWaTsMs = Number.MAX_SAFE_INTEGER;
      if (lowerWaTsMs > upperWaTsMs) {
        [lowerWaTsMs, upperWaTsMs] = [upperWaTsMs, lowerWaTsMs];
      }
      const db = getMessageVaultDb();
      const exclude = new Set(excludeMsgIds);
      // Type exclusion: the ordinal slot-matcher aligns N revoked DOM
      // rows to N captured candidates by chronological position. Voice
      // notes are captured ASYNCHRONOUSLY (bytes fetched after arrival),
      // so at deletion time they're an unreliable member of the window —
      // present sometimes, absent others — which shifts the ordinal math
      // and mis-assigns (or drops) text/image matches. Callers that want
      // deterministic text/image matching exclude 'ptt'/'audio'; voice-
      // note recovery uses the persisted-mapping / direct-id paths that
      // don't depend on window alignment.
      const excludeTypes = new Set((opts.excludeTypes ?? []).map((t) => t.toLowerCase()));
      const rows = await db.messages
        .where('[chatId+whatsappTimestamp]')
        .between([chatId, lowerWaTsMs], [chatId, upperWaTsMs], true, true)
        .toArray();
      return rows
        .filter((m) => !exclude.has(m.msgId))
        .filter((m) => !excludeTypes.has(String(m.type ?? '').toLowerCase()))
        .sort((a, b) => a.whatsappTimestamp - b.whatsappTimestamp);
    },
    [],
  );

/** Context-based matching — PHASE 4's resilient fallback.
 *
 *  When a placeholder is found in the DOM, the rows IMMEDIATELY
 *  surrounding it carry data-ids that DO match vault rows (those are
 *  real, non-revoked messages). Their WA timestamps give us a
 *  CHRONOLOGICAL WINDOW: the placeholder used to sit between them.
 *
 *  This function finds the single captured message in `chatId` whose
 *  `whatsappTimestamp` falls in `[lowerWaTsMs, upperWaTsMs]`. If
 *  multiple match, the one closest to the WINDOW MIDPOINT wins. If
 *  none match, returns null — callers should NOT chip a guess in
 *  that case.
 *
 *  Bounds may be open (Infinity/0) when the placeholder is at the
 *  top or bottom of the visible chat.
 *
 *  Independent of deletion events — works even when the extension
 *  was reloaded or the deletion fired while we were offline. */
export const findByContext = async (
  chatId: WaChatId,
  lowerWaTsMs: number,
  upperWaTsMs: number,
  options: {
    /** Exclude these msgIds — typically the ids of the surrounding
     *  real rows themselves so we don't accidentally chip THEM. */
    excludeMsgIds?: ReadonlyArray<WaMessageId>;
    /** Limit to a specific WA message type (chat / image / …). */
    type?: string;
  } = {},
): Promise<BackedUpMessage | null> =>
  safe(
    'findByContext',
    async () => {
      if (!chatId) return null;
      if (!Number.isFinite(lowerWaTsMs)) lowerWaTsMs = 0;
      if (!Number.isFinite(upperWaTsMs)) upperWaTsMs = Number.MAX_SAFE_INTEGER;
      if (lowerWaTsMs > upperWaTsMs) [lowerWaTsMs, upperWaTsMs] = [upperWaTsMs, lowerWaTsMs];

      const db = getMessageVaultDb();
      const excluded = new Set(options.excludeMsgIds ?? []);

      // O(log n) range scan via the [chatId+whatsappTimestamp] compound
      // index — never reads rows outside the window.
      const candidates = await db.messages
        .where('[chatId+whatsappTimestamp]')
        .between([chatId, lowerWaTsMs], [chatId, upperWaTsMs], true, true)
        .toArray();

      if (candidates.length === 0) return null;

      const filtered = candidates.filter((m) => {
        if (excluded.has(m.msgId)) return false;
        if (options.type && m.type && m.type !== options.type) return false;
        return true;
      });
      if (filtered.length === 0) return null;
      if (filtered.length === 1) return filtered[0];

      // Multiple candidates fit — pick the one closest to the
      // window's midpoint. This is the most likely "the missing slot"
      // candidate.
      const midpoint = (lowerWaTsMs + upperWaTsMs) / 2;
      let best = filtered[0];
      let bestDelta = Math.abs(best.whatsappTimestamp - midpoint);
      for (let i = 1; i < filtered.length; i++) {
        const d = Math.abs(filtered[i].whatsappTimestamp - midpoint);
        if (d < bestDelta) {
          best = filtered[i];
          bestDelta = d;
        }
      }
      return best;
    },
    null,
  );

/** PUBLIC RECOVERY API per PHASE 5 of the spec.
 *
 *  Caller provides whatever identifying info it has — primary
 *  `messageKey` always preferred, then fallbacks. Returns a stable
 *  shape regardless of whether recovery succeeded. The caller's
 *  UI logic never has to inspect the database directly. */
export const recoverDeletedMessage = async (input: {
  /** Primary key — try this first. */
  messageKey?: string;
  /** Chat the placeholder is in — needed for fallbacks. */
  chatId?: string;
  /** Lower/upper bounds for context-based matching. */
  lowerWaTsMs?: number;
  upperWaTsMs?: number;
  /** Exclude these msgIds during context match (the surrounding
   *  rows that we're NOT trying to chip). */
  excludeMsgIds?: ReadonlyArray<string>;
}): Promise<RecoveryResult> => {
  const empty: RecoveryResult = {
    recovered: false,
    originalText: '',
    originalTimestamp: 0,
    originalMessage: null,
  };

  // TIER 1 — messageKey direct lookup. Per spec: "If messageKey
  // matching succeeds, stop immediately."
  if (input.messageKey) {
    const row = await getMessage(input.messageKey);
    if (row) {
      return {
        recovered: true,
        originalText: row.body,
        originalTimestamp: row.whatsappTimestamp,
        originalMessage: row,
        matchedBy: 'messageKey',
      };
    }
  }

  // TIER 2 — context-based matching. Resilient to deletion-event
  // misses (PHASE 3's "must work even when WA was closed").
  if (
    input.chatId &&
    (input.lowerWaTsMs !== undefined || input.upperWaTsMs !== undefined)
  ) {
    const row = await findByContext(
      input.chatId,
      input.lowerWaTsMs ?? 0,
      input.upperWaTsMs ?? Number.MAX_SAFE_INTEGER,
      { excludeMsgIds: input.excludeMsgIds },
    );
    if (row) {
      return {
        recovered: true,
        originalText: row.body,
        originalTimestamp: row.whatsappTimestamp,
        originalMessage: row,
        matchedBy: 'context',
      };
    }
  }

  // TIER 3 — last resort: most recent in chat. Only when we have
  // no temporal bounds. Imperfect but matches the common case.
  if (input.chatId) {
    const row = await getMostRecentInChat(input.chatId, input.excludeMsgIds);
    if (row) {
      return {
        recovered: true,
        originalText: row.body,
        originalTimestamp: row.whatsappTimestamp,
        originalMessage: row,
        matchedBy: 'recent',
      };
    }
  }

  return empty;
};

/** Find the captured message in `chatId` whose `whatsappTimestamp` is
 *  closest to `targetMs`. Used by the deletion bridge: WA preserves
 *  the original message's `t` on the revoke event payload, so we can
 *  match by send-time even when the ids don't line up.
 *
 *  `toleranceMs` rejects matches that are further apart than the
 *  caller's tolerance — better to admit "we don't know" than to chip
 *  a totally unrelated message. */
export const getNearestInChatByTimestamp = async (
  chatId: WaChatId,
  targetMs: number,
  toleranceMs: number = 120_000,
): Promise<BackedUpMessage | null> =>
  safe(
    'getNearestInChatByTimestamp',
    async () => {
      if (!chatId || !targetMs) return null;
      const db = getMessageVaultDb();
      const candidates = await db.messages
        .where('[chatId+capturedAt]')
        .between([chatId, 0], [chatId, Infinity])
        .toArray();
      let best: BackedUpMessage | null = null;
      let bestDelta = Infinity;
      for (const m of candidates) {
        const delta = Math.abs(m.whatsappTimestamp - targetMs);
        if (delta < bestDelta && delta <= toleranceMs) {
          best = m;
          bestDelta = delta;
        }
      }
      return best;
    },
    null,
  );

/* ─────────────────────────────────────────────────────────────────── */
/* OPPORTUNISTIC MEDIA RE-QUEUE                                          */
/* ─────────────────────────────────────────────────────────────────── */

/** Pure predicate: should this vault row be flipped back into the media-upload
 *  queue on a chat re-open? Yes only for a row that (a) isn't already queued,
 *  (b) isn't already backed up, (c) has downloadable media of an ENABLED
 *  category, and (d) doesn't carry its own audio bytes (those use a different
 *  path). Extracted + pure so the eligibility rules are unit-tested directly. */
export const shouldRequeueMediaRow = (
  row: Pick<BackedUpMessage, 'mediaSynced' | 'mediaPointer' | 'audioBase64' | 'hadMedia' | 'type'>,
  isTypeEnabled: (type: string) => boolean,
): boolean => {
  if (row.mediaSynced === 0) return false;   // already queued
  if (row.mediaPointer) return false;        // already backed up
  if (row.audioBase64) return false;         // audio carries its own bytes
  // Eligibility keys on the message TYPE (reliable), not `hadMedia` — the latter
  // is false for ~58% of images (WA hadn't loaded hasMedia/mediaKey at capture),
  // which is exactly what kept them out of backup. A bucket media type IS media.
  if (!isBucketMediaType(row.type)) return false;
  return isTypeEnabled(String(row.type ?? ''));
};

/** Re-queue a chat's failed-earlier media so the next backup run retries it.
 *
 *  Full-media backup can only download bytes for a message WhatsApp currently
 *  holds in its in-memory store. When the backfill first ran, media in chats
 *  that weren't loaded got `skipped` — marked `mediaSynced=1` with NO
 *  `mediaPointer` (done, but never uploaded). Opening/viewing a chat loads its
 *  messages back into the store, so that media is downloadable AGAIN. This flips
 *  those rows back to `mediaSynced=0` so the throttled media phase retries them.
 *
 *  Event-driven by design (called on chat open, never on a timer): a row that
 *  STILL can't download is re-`skipped` and won't retry until the chat is opened
 *  again — no background retry storm, no wasted cycles on evicted media.
 *
 *  Only touches ENABLED media categories (the caller passes `isTypeEnabled`,
 *  built from the user's backup config). Leaves alone: already-queued rows
 *  (`mediaSynced===0`), already-backed-up rows (`mediaPointer` set), audio rows
 *  (their bytes ride in the row via a different path), and byte-less rows
 *  (`!hadMedia`). The CALLER must gate on the backup opt-in being ON — this is
 *  never invoked when server backup is off. Returns the count re-queued. */
export const requeueChatMediaForBackup = async (
  chatId: WaChatId,
  isTypeEnabled: (type: string) => boolean,
  cap = 500,
): Promise<number> =>
  safe(
    'requeueChatMediaForBackup',
    async () => {
      if (!chatId) return 0;
      const db = getMessageVaultDb();
      let requeued = 0;
      await db.messages
        .where('[chatId+capturedAt]')
        .between([chatId, 0], [chatId, Infinity])
        .modify((row) => {
          if (requeued >= cap) return;
          if (!shouldRequeueMediaRow(row, isTypeEnabled)) return;
          row.mediaSynced = 0;
          requeued++;
        });
      if (requeued > 0) {
        console.log('[bt-vault] re-queued media for backup retry', { chatId, requeued });
      }
      return requeued;
    },
    0,
  );

/* ─────────────────────────────────────────────────────────────────── */
/* PLACEHOLDER MAPPINGS — persisted DOM-id → body                       */
/* ─────────────────────────────────────────────────────────────────── */

/** Save a placeholder→body mapping. Called once per deletion (when
 *  the injection layer has confirmed the chip belongs to this DOM
 *  data-id). After this, the chip survives virtualization re-renders
 *  and page reloads — the injection just looks up by data-id. */
export const recordPlaceholderMapping = async (
  placeholderDataId: string,
  source: {
    chatId: WaChatId;
    body: string;
    caption?: string;
    audioBase64?: string;
    audioMimetype?: string;
    hadMedia: boolean;
    authorName: string;
    whatsappTimestamp: number;
  },
): Promise<boolean> =>
  safe(
    'recordPlaceholderMapping',
    async () => {
      if (!placeholderDataId || (!source.body && !source.audioBase64)) return false;
      const db = getMessageVaultDb();
      // `put` with unique-key replaces, so re-recording is a no-op
      // (timestamp updates are intentional — record the latest match).
      await db.placeholderMappings.put({
        placeholderDataId,
        chatId: source.chatId,
        body: source.body,
        caption: source.caption,
        audioBase64: source.audioBase64,
        audioMimetype: source.audioMimetype,
        hadMedia: source.hadMedia,
        authorName: source.authorName,
        whatsappTimestamp: source.whatsappTimestamp,
        recordedAt: Date.now(),
      });
      return true;
    },
    false,
  );

/** Lookup the persisted body for a DOM placeholder. Called first by
 *  the injection on every sweep — the FAST path. */
export const getPlaceholderMapping = async (
  placeholderDataId: string,
): Promise<PlaceholderMapping | null> =>
  safe(
    'getPlaceholderMapping',
    async () => {
      if (!placeholderDataId) return null;
      const db = getMessageVaultDb();
      const row = await db.placeholderMappings
        .where('placeholderDataId')
        .equals(placeholderDataId)
        .first();
      return row ?? null;
    },
    null,
  );

/** Count of persisted placeholder mappings — for diagnostics. */
export const countPlaceholderMappings = async (): Promise<number> =>
  safe(
    'countPlaceholderMappings',
    async () => getMessageVaultDb().placeholderMappings.count(),
    0,
  );

/* ─────────────────────────────────────────────────────────────────── */
/* RETENTION                                                            */
/* ─────────────────────────────────────────────────────────────────── */

const DAY_MS = 24 * 60 * 60 * 1000;

/** Default local-vault retention, in days.
 *
 *  Governs deleted-message recovery for every user (the vault is captured
 *  regardless of the server-backup opt-in). The rationale: WA's
 *  delete-for-everyone window is limited, so keeping recovery data much
 *  longer doesn't help the user but does grow the DB. Callers pass the
 *  user's configured `backup.vaultRetentionDays` (which defaults to this),
 *  giving ONE source of truth for local retention shared with the backup
 *  uploader's prune. */
export const DEFAULT_VAULT_RETENTION_DAYS = 2;

/** A row is safe to retention-prune only when it's OLDER than the cutoff AND —
 *  when server backup is on (`keepBackupPending`) — its backup is COMPLETE (both
 *  the message row `synced` and its media `mediaSynced`). Extracted + pure so the
 *  rule is unit-tested. Deleting a row whose media hasn't uploaded yet is what
 *  loses media forever: the row's gone from the vault, so its bytes can never be
 *  backed up and its server row is stuck without a full-media pointer. */
export const isRetentionPrunable = (
  row: Pick<BackedUpMessage, 'synced' | 'mediaSynced' | 'mediaSkipReason'>,
  keepBackupPending: boolean,
): boolean => {
  if (!keepBackupPending) return true;
  if (row.synced !== 1) return false;
  // `mediaSynced === 1` means the bytes are safely uploaded. A `mediaSkipReason`
  // means the opposite conclusion by the same authority: the backup engine has
  // already DECIDED this row's media is not going to be fetched (category off,
  // over the size cap, or gone from WhatsApp). Waiting for a `mediaSynced` that
  // can never arrive is not caution — it pins the row in IndexedDB forever.
  // With the shipped default (backup on, videos off) that is every video the
  // user has ever received, which is exactly the unbounded growth the retention
  // sweep exists to prevent.
  return row.mediaSynced === 1 || Boolean(row.mediaSkipReason);
};

/** Drop every message row (and same-age placeholder mapping) older than
 *  `retentionDays`. Single retention authority for the local vault — the
 *  deleted-message-recovery sweep and the backup uploader both call this
 *  with the user's `vaultRetentionDays`. Returns the count removed.
 *
 *  `opts.keepBackupPending` (set when server backup is ON): a row past retention
 *  is KEPT until its backup finishes, so media that hasn't uploaded yet still
 *  gets a chance — otherwise the row is deleted and its media can never be backed
 *  up. Rows resolve (uploaded or SKIP-marked) as the throttled backup runs drain
 *  the queue, then become prunable, so the vault never grows unbounded. */
export const purgeVaultBeyondRetention = async (
  retentionDays: number = DEFAULT_VAULT_RETENTION_DAYS,
  opts: { keepBackupPending?: boolean } = {},
): Promise<number> =>
  safe(
    'purgeVaultBeyondRetention',
    async () => {
      const days = Number.isFinite(retentionDays) && retentionDays > 0
        ? retentionDays
        : DEFAULT_VAULT_RETENTION_DAYS;
      const cutoff = Date.now() - days * DAY_MS;
      const keepBackupPending = opts.keepBackupPending === true;
      const db = getMessageVaultDb();
      let removed = 0;
      await db.transaction(
        'rw',
        db.messages,
        db.placeholderMappings,
        async () => {
          const old = db.messages.where('capturedAt').below(cutoff);
          removed = keepBackupPending
            ? await old.filter((r) => isRetentionPrunable(r, true)).delete()
            : await old.delete();
          await db.placeholderMappings
            .where('recordedAt')
            .below(cutoff)
            .delete();
        },
      );
      return removed;
    },
    0,
  );

/** Drop everything. Used by Display Settings → "Wipe vault". */
export const wipeAll = async (): Promise<void> =>
  safe(
    'wipeAll',
    async () => {
      const db = getMessageVaultDb();
      await db.transaction('rw', db.messages, db.placeholderMappings, async () => {
        await db.messages.clear();
        await db.placeholderMappings.clear();
      });
    },
    undefined,
  );
